/**
 * JournalPage — "Journal d'audit": the lab-scoped AuditLog read, filtered (entité / action / actor /
 * capability / date range) + SERVER-paginated. The journal exposes metadata only (entité, action, actor,
 * capability, before/after diffs, timestamp); services that touch PHI stamp diffs not bodies (L-4), so
 * this never renders patient data — it pages what the wildcard audit hook persisted.
 *
 * Gating: read on audit.journal.read_all (the owner-scoped trio) — screen ForbiddenState + nav-item cap.
 * Full 5-state DoD; the from>to case is surfaced inline (mirrors the server's clean VALIDATION error).
 */
import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Drawer,
  ErrorState,
  ForbiddenState,
  Input,
  LoadingState,
  OfflineState,
  Pagination,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ADMIN_CAPS } from "../caps";
import { useJournal, useLabId } from "../api";
import type { JournalEntry, JournalFilters } from "../types";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { JsonView } from "../components/JsonView";
import { actorLabel, formatDateTime, previewJson } from "../lib";

const PAGE_SIZE = 25;

interface DraftFilters {
  entite: string;
  action: string;
  actor: string;
  capability_cle: string;
  from: string;
  to: string;
}

const EMPTY_DRAFT: DraftFilters = { entite: "", action: "", actor: "", capability_cle: "", from: "", to: "" };

export function JournalPage() {
  const { t, locale } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(ADMIN_CAPS.journalReadAll);

  const [draft, setDraft] = useState<DraftFilters>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<JournalFilters>({});
  const [page, setPage] = useState(1);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [detail, setDetail] = useState<JournalEntry | null>(null);

  const filters: JournalFilters = useMemo(
    () => ({ ...applied, page, page_size: PAGE_SIZE }),
    [applied, page],
  );

  const query = useJournal(labId, filters, canRead && online);
  const data = query.data;

  /** Convert a datetime-local value to an ISO 8601 string (the server validates `datetime()`). */
  const toIso = (local: string): string | undefined => {
    if (!local) return undefined;
    const d = new Date(local);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
  };

  const apply = () => {
    setRangeError(null);
    const from = toIso(draft.from);
    const to = toIso(draft.to);
    if (from && to && new Date(from) > new Date(to)) {
      setRangeError(t("admin.journal.range.invalid"));
      return;
    }
    const next: JournalFilters = {};
    if (draft.entite.trim()) next.entite = draft.entite.trim();
    if (draft.action.trim()) next.action = draft.action.trim();
    if (draft.capability_cle.trim()) next.capability_cle = draft.capability_cle.trim();
    if (draft.actor.trim() && Number.isFinite(Number(draft.actor))) next.actor = Number(draft.actor);
    if (from) next.from = from;
    if (to) next.to = to;
    setApplied(next);
    setPage(1);
  };

  const reset = () => {
    setDraft(EMPTY_DRAFT);
    setApplied({});
    setRangeError(null);
    setPage(1);
  };

  const setDraftField = (key: keyof DraftFilters, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const columns: ReadonlyArray<Column<JournalEntry>> = [
    {
      key: "at",
      header: t("admin.journal.col.at"),
      mono: true,
      cell: (e) => <span className="nv-ltr-island">{formatDateTime(e.at, locale)}</span>,
    },
    { key: "entite", header: t("admin.journal.col.entite"), mono: true, cell: (e) => <span className="nv-ltr-island">{e.entite}</span> },
    { key: "action", header: t("admin.journal.col.action"), cell: (e) => <Badge tone="neutral" glyph="▸">{e.action}</Badge> },
    { key: "actor", header: t("admin.journal.col.actor"), mono: true, cell: (e) => <span className="nv-ltr-island">{actorLabel(e.utilisateurId)}</span> },
    {
      key: "capability",
      header: t("admin.journal.col.capability"),
      mono: true,
      cell: (e) => <span className="nv-ltr-island">{e.capabilityCle ?? "—"}</span>,
    },
    {
      key: "details",
      header: t("admin.journal.col.details"),
      align: "end",
      cell: (e) => (
        <Button size="sm" variant="ghost" onClick={() => setDetail(e)} title={previewJson(e.apres)}>
          {t("admin.journal.details")}
        </Button>
      ),
    },
  ];

  const header = <PageHeader title={t("admin.journal.title")} subtitle={t("admin.journal.subtitle")} />;

  if (!canRead) return <ForbiddenState requiredCapabilities={[ADMIN_CAPS.journalReadAll]} />;

  const filterBar = (
    <Card>
      <div style={{ display: "grid", gap: "var(--nv-s-3)", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <LabeledInput label={t("admin.journal.filter.entite")} value={draft.entite} onChange={(v) => setDraftField("entite", v)} mono />
        <LabeledInput label={t("admin.journal.filter.action")} value={draft.action} onChange={(v) => setDraftField("action", v)} mono />
        <LabeledInput label={t("admin.journal.filter.actor")} value={draft.actor} onChange={(v) => setDraftField("actor", v)} mono inputMode="numeric" />
        <LabeledInput label={t("admin.journal.filter.capability")} value={draft.capability_cle} onChange={(v) => setDraftField("capability_cle", v)} mono />
        <LabeledInput label={t("admin.journal.filter.from")} value={draft.from} onChange={(v) => setDraftField("from", v)} type="datetime-local" />
        <LabeledInput label={t("admin.journal.filter.to")} value={draft.to} onChange={(v) => setDraftField("to", v)} type="datetime-local" />
      </div>
      {rangeError && (
        <p role="alert" style={{ margin: "var(--nv-s-3) 0 0", color: "var(--nv-danger, var(--nv-critical))" }}>
          {rangeError}
        </p>
      )}
      <div style={{ display: "flex", gap: "var(--nv-s-2)", justifyContent: "flex-end", marginTop: "var(--nv-s-3)" }}>
        <Button variant="ghost" onClick={reset}>{t("admin.journal.filter.reset")}</Button>
        <Button onClick={apply}>{t("admin.journal.filter.apply")}</Button>
      </div>
    </Card>
  );

  if (!online) {
    return (
      <Screen>
        {header}
        {filterBar}
        <OfflineState onRetry={() => void query.refetch()} cached={!!data} />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}
      {filterBar}

      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)" }}>
          {query.isLoading ? (
            <LoadingState variant="table" rows={8} />
          ) : query.isError ? (
            <ErrorState message={query.error.message} code={query.error.code} onRetry={() => void query.refetch()} />
          ) : (
            <>
              <Table
                columns={columns}
                rows={data?.items ?? []}
                rowKey={(e) => e.id}
                caption={t("admin.journal.title")}
                emptyTitle={t("admin.journal.empty.title")}
                emptyDescription={t("admin.journal.empty.desc")}
              />
              {data && data.totalPages > 1 && (
                <Pagination
                  page={data.page}
                  pageCount={data.totalPages}
                  total={data.total}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </Card>

      <Drawer open={!!detail} onClose={() => setDetail(null)} title={t("admin.journal.detail.title")}>
        {detail && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--nv-s-2) var(--nv-s-4)", margin: 0 }}>
              <DtDd label={t("admin.journal.col.at")} value={formatDateTime(detail.at, locale)} mono />
              <DtDd label={t("admin.journal.col.entite")} value={`${detail.entite}${detail.entiteId ? ` · ${detail.entiteId}` : ""}`} mono />
              <DtDd label={t("admin.journal.col.action")} value={detail.action} />
              <DtDd label={t("admin.journal.col.actor")} value={actorLabel(detail.utilisateurId)} mono />
              <DtDd label={t("admin.journal.col.capability")} value={detail.capabilityCle ?? "—"} mono />
            </dl>
            <div>
              <h3 style={{ margin: "0 0 var(--nv-s-2)", fontSize: "var(--nv-fs-title)" }}>{t("admin.journal.detail.before")}</h3>
              <JsonView value={detail.avant} />
            </div>
            <div>
              <h3 style={{ margin: "0 0 var(--nv-s-2)", fontSize: "var(--nv-fs-title)" }}>{t("admin.journal.detail.after")}</h3>
              <JsonView value={detail.apres} />
            </div>
            <div>
              <h3 style={{ margin: "0 0 var(--nv-s-2)", fontSize: "var(--nv-fs-title)" }}>{t("admin.journal.detail.context")}</h3>
              <JsonView value={detail.contexte} />
            </div>
          </div>
        )}
      </Drawer>
    </Screen>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = "text",
  mono,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  mono?: boolean;
  inputMode?: "numeric";
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-1)" }}>
      <span style={{ color: "var(--nv-text-2, var(--nv-slate))", fontSize: "var(--nv-fs-label)" }}>{label}</span>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={mono ? "nv-ltr-island" : undefined}
        {...(inputMode ? { inputMode } : {})}
      />
    </label>
  );
}

function DtDd({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{label}</dt>
      <dd style={{ margin: 0 }} className={mono ? "nv-ltr-island" : undefined}>
        {value}
      </dd>
    </>
  );
}
